/**
 * App-wide image preloader / cache.
 *
 * Strategy: create an `Image()`, start the HTTP fetch, then call
 * `.decode()` to force the browser to decode the pixels NOW instead of
 * deferring decoding until the first paint. When a real `<img>` mounts
 * later with the same URL, both the bytes (browser HTTP cache) and the
 * decoded pixels (warm-path decode) are ready — paint is instant.
 *
 * Memory is bounded by a simple LRU cap so long sessions don't leak.
 */

type Entry = { img: HTMLImageElement; ready: Promise<void>; settled: boolean }

const MAX_ENTRIES = 250
const cache = new Map<string, Entry>()

function touch(url: string, entry: Entry) {
  // Re-insert to mark as most-recently-used (Map preserves insertion order)
  cache.delete(url)
  cache.set(url, entry)
}

function evictIfNeeded() {
  while (cache.size > MAX_ENTRIES) {
    const oldest = cache.keys().next().value
    if (!oldest) {
      break
    }
    cache.delete(oldest)
  }
}

export function preloadImage(url: string | null | undefined): Promise<void> {
  if (!url || typeof url !== 'string') {
    return Promise.resolve()
  }

  const existing = cache.get(url)
  if (existing) {
    touch(url, existing)
    return existing.ready
  }

  const img = new Image()
  img.decoding = 'async'
  ;(img as any).fetchPriority = 'high'

  const entry = { img, ready: null as unknown as Promise<void>, settled: false }
  const ready = new Promise<void>(resolve => {
    const done = () => {
      entry.settled = true
      resolve()
    }
    const tryDecode = () => {
      if (typeof img.decode === 'function') {
        img.decode().then(done, done)
      } else {
        done()
      }
    }
    if (img.complete && img.naturalWidth > 0) {
      tryDecode()
    } else {
      img.addEventListener('load', tryDecode, { once: true })
      img.addEventListener('error', done, { once: true })
    }
  })

  img.src = url
  entry.ready = ready
  cache.set(url, entry)
  evictIfNeeded()
  return ready
}

export function preloadImages(urls: Array<string | null | undefined>): Promise<void[]> {
  return Promise.all(urls.map(preloadImage))
}

export function hasPreloaded(url: string): boolean {
  return cache.has(url)
}

// True once the preload finished — decoded, or failed and ready for the caller's
// error fallback. Distinct from `hasPreloaded`, which is true the moment one starts.
export function isPreloadSettled(url: string | null | undefined): boolean {
  return !!url && !!cache.get(url)?.settled
}

export function clearImageCache(): void {
  cache.clear()
}
